import { create } from 'zustand'
import type { ClockState } from '@/domain/clock'
import { IDLE_CLOCK, elapsedMs, freezeClock, pauseClock, startClock } from '@/domain/clock'
import type { ChallengeWeek, Phase } from '@/domain/workout'

/** Momento monotónico: inmune a cambios de hora del sistema. */
export const now = (): number => performance.now()

/**
 * Dónde está el alumno dentro de un bloque:
 * - `briefing`: explicación previa, el reloj aún no corre.
 * - `work`: el bloque en marcha.
 * - `done`: bloque cerrado, esperando a pasar al siguiente.
 */
export type Stage = 'briefing' | 'work' | 'done'

interface SessionState {
  week: ChallengeWeek | null
  phaseIndex: number
  stage: Stage
  clock: ClockState
  /** Rondas contadas a mano en cada AMRAP, por id de fase. */
  rounds: Record<string, number>
  /** Bloques marcados en cada AFAP, por id de fase. */
  ticks: Record<string, boolean[]>
  /** Tiempo final de cada AFAP en segundos, por id de fase. */
  afapSeconds: Record<string, number>
  crewBonus: boolean
  /** La sesión ha terminado: toca la pantalla de cierre. */
  summary: boolean
}

interface SessionActions {
  open: (week: ChallengeWeek) => void
  close: () => void
  goToPhase: (index: number) => void
  nextPhase: () => void
  previousPhase: () => void
  restartPhase: () => void
  beginWork: () => void
  start: () => void
  pause: () => void
  /** Cierra la fase actual: por tiempo agotado o por completar la lista. */
  completePhase: (finalSeconds?: number) => void
  addRound: () => void
  removeRound: () => void
  toggleTick: (index: number) => void
  setCrewBonus: (value: boolean) => void
}

export type SessionStore = SessionState & SessionActions

const INITIAL: SessionState = {
  week: null,
  phaseIndex: 0,
  stage: 'briefing',
  clock: IDLE_CLOCK,
  rounds: {},
  ticks: {},
  afapSeconds: {},
  crewBonus: false,
  summary: false,
}

/**
 * Estado inicial de una fase. La explicación previa solo tiene sentido donde
 * hay ejercicios que enseñar: en un descanso se va directo al reloj.
 */
function stageFor(phase: Phase | undefined): Stage {
  if (!phase?.briefing) return 'work'
  return phase.kind === 'amrap' || phase.kind === 'afap' ? 'briefing' : 'work'
}

const phasesOf = (state: SessionState): readonly Phase[] => state.week?.workout.phases ?? []

const currentPhase = (state: SessionState): Phase | undefined => phasesOf(state)[state.phaseIndex]

export const useSessionStore = create<SessionStore>((set, get) => ({
  ...INITIAL,

  open: (week) => {
    const first = week.workout.phases[0]
    set({ ...INITIAL, week, stage: stageFor(first) })
  },

  close: () => set({ ...INITIAL }),

  goToPhase: (index) => {
    const phases = phasesOf(get())
    if (index < 0 || index >= phases.length) return
    set({ phaseIndex: index, stage: stageFor(phases[index]), clock: IDLE_CLOCK })
  },

  nextPhase: () => {
    const state = get()
    const phases = phasesOf(state)
    const next = state.phaseIndex + 1
    if (next >= phases.length) {
      set({ summary: true })
      return
    }

    // Encadenar bloques: si el siguiente no lleva explicación previa, el reloj
    // arranca solo. Nadie quiere pulsar dos botones entre serie y descanso.
    const stage = stageFor(phases[next])
    set({
      phaseIndex: next,
      stage,
      clock: stage === 'work' ? startClock(IDLE_CLOCK, now()) : IDLE_CLOCK,
    })
  },

  previousPhase: () => get().goToPhase(get().phaseIndex - 1),

  restartPhase: () => {
    const phase = currentPhase(get())
    if (!phase) return
    // Reiniciar es empezar el bloque de cero: el reloj, las marcas y las rondas.
    set((state) => ({
      stage: 'work',
      clock: IDLE_CLOCK,
      ticks: { ...state.ticks, [phase.id]: [] },
      rounds: { ...state.rounds, [phase.id]: 0 },
    }))
  },

  beginWork: () => set({ stage: 'work' }),

  start: () => set((state) => ({ stage: 'work', clock: startClock(state.clock, now()) })),

  pause: () => set((state) => ({ clock: pauseClock(state.clock, now()) })),

  completePhase: (finalSeconds) => {
    const state = get()
    const phase = currentPhase(state)
    if (!phase) return

    const seconds = finalSeconds ?? elapsedMs(state.clock, now()) / 1000
    set({
      stage: 'done',
      clock: freezeClock(state.clock, now()),
      afapSeconds:
        phase.kind === 'afap'
          ? { ...state.afapSeconds, [phase.id]: Math.round(seconds) }
          : state.afapSeconds,
    })
  },

  addRound: () => {
    const phase = currentPhase(get())
    if (!phase) return
    set((state) => ({
      rounds: { ...state.rounds, [phase.id]: (state.rounds[phase.id] ?? 0) + 1 },
    }))
  },

  removeRound: () => {
    const phase = currentPhase(get())
    if (!phase) return
    set((state) => ({
      rounds: { ...state.rounds, [phase.id]: Math.max(0, (state.rounds[phase.id] ?? 0) - 1) },
    }))
  },

  toggleTick: (index) => {
    const state = get()
    const phase = currentPhase(state)
    if (!phase || phase.kind !== 'afap') return

    const current = state.ticks[phase.id] ?? []
    const next = phase.blocks.map((_, i) => (i === index ? !current[i] : (current[i] ?? false)))
    set({ ticks: { ...state.ticks, [phase.id]: next } })
  },

  setCrewBonus: (value) => set({ crewBonus: value }),
}))

/* ── Selectores ───────────────────────────────────────────────────────── */

export const selectPhase = (state: SessionStore): Phase | undefined => currentPhase(state)

export const selectPhases = (state: SessionStore): readonly Phase[] => phasesOf(state)

export const selectIsLastPhase = (state: SessionStore): boolean =>
  state.phaseIndex >= phasesOf(state).length - 1

/**
 * Devuelve el array tal cual está guardado, sin normalizar: un selector que
 * construyera uno nuevo en cada lectura dispararía renders sin fin.
 * Normalizar la longitud es cosa del componente, con `useMemo`.
 */
export const selectStoredTicks = (state: SessionStore): boolean[] | undefined => {
  const phase = currentPhase(state)
  return phase ? state.ticks[phase.id] : undefined
}

export const selectRounds = (state: SessionStore): number => {
  const phase = currentPhase(state)
  return phase ? (state.rounds[phase.id] ?? 0) : 0
}
